import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import Toast from '../common/Toast';
import AuthContext from '../../context/AuthContext';
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
  ListGroup,
  Table,
  InputGroup,
  Badge
} from 'react-bootstrap';
import {
  FaSave,
  FaPrint,
  FaPlus,
  FaTimes,
  FaSearch,
  FaCalendarAlt,
  FaUpload,
  FaTrash,
  FaCheck
} from 'react-icons/fa';

// Add print styles
const PrintStyles = () => (
  <style>
    {`
      @media print {
        /* Hide everything except print view */
        body * {
          visibility: hidden;
        }
        .print-view, .print-view * {
          visibility: visible;
        }
        .print-view {
          position: absolute;
          width: 100%;
          top: 0;
          left: 0;
          right: 0;
          padding: 0;
          margin: 0;
        }
        
        /* Page setup */
        @page {
          size: A4 portrait;
          margin: 15mm 10mm 20mm 10mm !important;
        }
        
        /* CRITICAL: Force page breaks - each page is separate */
        .first-page-content {
          page-break-after: always !important;
          break-after: page !important;
          display: block !important;
          width: 100% !important;
          min-height: 100vh !important;
          overflow: visible !important;
          padding: 20px !important;
          box-sizing: border-box !important;
        }
        
        .ultrasound-report-section {
          page-break-before: always !important;
          break-before: page !important;
          page-break-after: auto !important;
          display: block !important;
          width: 100% !important;
          min-height: 100vh !important;
          padding: 20px !important;
          box-sizing: border-box !important;
        }
        
        /* Clinic header styles */
        .clinic-header {
          display: flex !important;
          align-items: center !important;
          border-bottom: 2px solid #333 !important;
          padding-bottom: 10px !important;
          margin-bottom: 15px !important;
        }
        
        .clinic-header img {
          max-width: 80px !important;
          height: auto !important;
          margin-right: 15px !important;
        }
        
        .clinic-header h2 {
          margin: 0 !important;
          font-family: Georgia, serif !important;
          color: #1a5276 !important;
          font-weight: bold !important;
          letter-spacing: 1px !important;
        }
        
        /* Page setup */
        @page {
          size: A4;
          margin: 15mm 10mm 20mm 10mm !important;
        }
        
        /* Footer styles */
        .report-footer {
          position: fixed !important;
          bottom: 0 !important;
          left: 0 !important;
          right: 0 !important;
          padding: 8px 20px !important;
          border-top: 1px solid #ddd !important;
          background: white !important;
          font-size: 8px !important;
          text-align: center !important;
        }
        
        /* Content padding for footer */
        .report-content {
          padding-bottom: 30mm !important;
        }
        
        /* Prevent orphaned content */
        .diagnosis-section,
        .medication-section {
          page-break-inside: avoid !important;
        }
        
        /* Allow natural page breaks for long content */
        table.medication-table {
          page-break-inside: auto !important;
        }
        
        table.medication-table tr {
          page-break-inside: avoid !important;
        }
        
        /* Doctor signatures */
        .doctor-signatures {
          margin-top: 15px !important;
          text-align: center;
          margin-bottom: 60px !important;
        }
        
        .doctor-signature {
          margin-top: 15px !important;
          text-align: center;
          width: 100px !important;
          display: inline-block;
          margin-right: 10px !important;
          margin-left: 10px !important;
        }
        
        .doctor-signature p {
          margin-bottom: 0 !important;
          font-size: 10px !important;
        }
        
        /* Compact table styling for first page */
        table.medication-table {
          width: 100%;
          margin-bottom: 10px !important;
          border-collapse: collapse;
          max-height: 100% !important;
          font-size: 10px !important;
        }
        
        table.medication-table th, 
        table.medication-table td {
          padding: 3px 4px !important;
          font-size: 10px !important;
          border: 1px solid #ddd;
          word-break: normal;
        }
        
        table.medication-table th {
          background-color: #f4f4f4;
          font-weight: bold;
        }
        
        /* Compact spacing */
        .diagnosis {
          margin-bottom: 8px !important;
        }
        
        h5 {
          font-size: 12px !important;
          margin-top: 5px !important;
          margin-bottom: 5px !important;
        }
        
        /* Ultrasound images styles */
        .ultrasound-images {
          margin: 15px 0 !important;
          page-break-inside: avoid !important;
          width: 100% !important;
        }
        .ultrasound-images-container {
          display: grid !important;
          grid-template-columns: 1fr 1fr !important;
          grid-gap: 15px !important;
          width: 100% !important;
        }
        .ultrasound-image-container {
          margin-bottom: 15px !important;
          text-align: center !important;
          page-break-inside: avoid !important;
        }
        
        /* Force horizontally paired images with stricter rules */
        .ultrasound-images table {
          width: 100% !important;
          table-layout: fixed !important;
          border-collapse: collapse !important;
        }
        .ultrasound-images tr {
          display: flex !important;
          flex-direction: row !important;
          width: 100% !important;
        }
        .ultrasound-images td {
          display: inline-block !important;
          width: 50% !important;
          max-width: 50% !important;
          float: left !important;
          box-sizing: border-box !important;
          padding: 5px !important;
        }
        .ultrasound-images img {
          max-width: 100% !important;
          border: 1px solid #ddd !important;
          background-color: #f8f9fa !important;
          padding: 4px !important;
          display: block !important;
          margin: 0 auto !important;
          height: auto !important;
        }
      }
    `}
  </style>
);

// Add debounce function for search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const CreateReport = () => {
  const { patientId, id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isEditMode = !!id;
  const reportRef = useRef();

  // Check permission on component mount
  useEffect(() => {
    if (user && user.role !== 'admin') {
      if (isEditMode && !user.permissions?.editReportAction) {
        navigate('/reports');
      } else if (!isEditMode && !user.permissions?.newReport) {
        navigate('/patients');
      }
    }
  }, [user, navigate, isEditMode]);

  const [patient, setPatient] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [medicineSearch, setMedicineSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  // Form state
  const [formData, setFormData] = useState({
    patient: patientId,
    doctor: '',
    diagnosis: '',
    medications: [],
    ultrasoundReport: {
      hasUltrasound: false,
      type: 'Abdominal',
      findings: '',
      impression: '',
      recommendations: '',
      images: [],
      gestationalAge: '',
      fetalHeartRate: '',
      biparietalDiameter: '',
      headCircumference: '',
      abdominalCircumference: '',
      femurLength: '',
      fetalPresentation: '',
      fetalLie: '',
      fetalMovement: '',
      cardiacActivity: '',
      placentaPosition: '',
      amniotic: '',
      gestationalSac: '',
      crownRumpLength: '',
      cardiacFlicker: '',
      measurements: {},
      organSizes: {}
    },
    doctorAdvice: '',
    followUpDate: '',
    billNumber: '',
    signedByDoctors: [],
    bloodPressure: {
      systolic: '',
      diastolic: ''
    }
  });

  // Temp state for new medication
  const [newMedication, setNewMedication] = useState({
    medicine: '',
    dosage: {
      morning: false,
      afternoon: false,
      evening: false,
      night: false,
      morningBeforeFood: false,
      morningAfterFood: false,
      afternoonBeforeFood: false,
      afternoonAfterFood: false,
      eveningBeforeFood: false,
      eveningAfterFood: false,
      nightBeforeFood: false,
      nightAfterFood: false
    },
    duration: '',
    instructions: '',
    providedFromStock: false,
    quantity: 0
  });

  // Add debounced search term
  const debouncedMedicineSearch = useDebounce(medicineSearch, 300); // 300ms delay

  // Function to fetch bill number for patient's today appointment
  const fetchBillNumber = async (patientInternalId) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const appointmentsRes = await axios.get(`/api/appointments.php?patientId=${patientInternalId}&startDate=${today}&endDate=${today}`);
      
      if (appointmentsRes.data && appointmentsRes.data.length > 0) {
        const todayAppointment = appointmentsRes.data[0];
        if (todayAppointment.billNumber) {
          setFormData(prev => ({
            ...prev,
            billNumber: todayAppointment.billNumber
          }));
        }
      }
    } catch (err) {
      console.error("Error fetching bill number:", err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // If in edit mode, fetch report data first
        if (isEditMode && id) {
          try {
            const reportRes = await axios.get(`/api/reports.php?id=${id}`);
            const reportData = reportRes.data;
            
            // Set patient from report data
            setPatient(reportData.Patient || reportData.patient);
            
            // Populate form with existing report data
            // Use patient.id (numeric database ID) not patientId (string identifier)
            setFormData({
              patient: reportData.patient?.id || reportData.patientId,
              doctor: reportData.doctor?.id || reportData.doctorId,
              diagnosis: reportData.diagnosis || '',
              medications: reportData.medications || [],
              ultrasoundReport: reportData.ultrasoundReport || {
                hasUltrasound: false,
                type: 'Abdominal',
                findings: '',
                impression: '',
                recommendations: '',
                images: [],
                gestationalAge: '',
                fetalHeartRate: '',
                biparietalDiameter: '',
                headCircumference: '',
                abdominalCircumference: '',
                femurLength: '',
                fetalPresentation: '',
                fetalLie: '',
                fetalMovement: '',
                cardiacActivity: '',
                placentaPosition: '',
                amniotic: '',
                gestationalSac: '',
                crownRumpLength: '',
                cardiacFlicker: ''
              },
              doctorAdvice: reportData.doctorAdvice || '',
              followUpDate: reportData.followUpDate || '',
              billNumber: reportData.billNumber || '',
              signedByDoctors: reportData.signedByDoctors || [],
              bloodPressure: reportData.bloodPressure || { systolic: '', diastolic: '' }
            });
          } catch (reportErr) {
            console.error("Error fetching report:", reportErr);
            setError(`Failed to load report data: ${reportErr.response?.data?.msg || reportErr.message}`);
          }
        } else if (patientId) {
          // Create mode - fetch patient data
          // Check if patientId is a string (patient code) or numeric (internal ID)
          console.log("Fetching patient with ID:", patientId, "Type:", typeof patientId);
          
          try {
            // First try to fetch by internal ID (numeric)
            let patientRes;
            
            // If patientId contains letters or hyphens, it's a patient code, need to search
            if (isNaN(patientId) || patientId.includes('-')) {
              console.log("Patient ID is a code, searching all patients...");
              // Fetch all patients and find by patientId code
              const allPatientsRes = await axios.get('/api/patients.php');
              const foundPatient = allPatientsRes.data.find(p => p.patientId === patientId);
              
              if (foundPatient) {
                console.log("Found patient by code:", foundPatient);
                setPatient(foundPatient);
                // Update formData with the correct internal ID
                setFormData(prev => ({
                  ...prev,
                  patient: foundPatient.id
                }));
                
                // Fetch bill number for today's appointment
                await fetchBillNumber(foundPatient.id);
              } else {
                throw new Error(`Patient with code ${patientId} not found`);
              }
            } else {
              // It's a numeric ID, fetch directly
              console.log("Patient ID is numeric, fetching directly...");
              patientRes = await axios.get(`/api/patients.php?id=${patientId}`);
              setPatient(patientRes.data);
              console.log("Patient data loaded:", patientRes.data);
              // Update formData with the internal ID
              setFormData(prev => ({
                ...prev,
                patient: patientRes.data.id
              }));
              
              // Fetch bill number for today's appointment
              await fetchBillNumber(patientRes.data.id);
            }
          } catch (patientErr) {
            console.error("Error fetching patient:", patientErr);
            setError(`Failed to load patient data: ${patientErr.response?.data?.msg || patientErr.message}`);
          }
        }

        // Fetch active doctors
        const doctorsRes = await axios.get('/api/doctors.php');
        setDoctors(doctorsRes.data);
        
        if (doctorsRes.data.length > 0) {
          setFormData(prev => ({
            ...prev,
            doctor: doctorsRes.data[0].id
          }));
        }

        // Fetch medicines
        const medicinesRes = await axios.get('/api/medicines.php');
        console.log("Medicines API response:", medicinesRes.data);
        // Fix: Correctly access the medicines property in the response
        setMedicines(medicinesRes.data || []);
        console.log("Medicines after setting state:", medicinesRes.data || []);
        
        // Fetch clinic info
        try {
          const clinicRes = await axios.get('/api/clinic.php');
          setClinic(clinicRes.data);
          console.log("Clinic data:", clinicRes.data);
        } catch (clinicErr) {
          console.error("Error fetching clinic info:", clinicErr);
        }

        setLoading(false);
      } catch (err) {
        console.error(err);
        setError('Failed to load data');
        setLoading(false);
      }
    };

    fetchData();
  }, [patientId, id, isEditMode]);

  const handleInputChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleNewMedicationChange = (field, value) => {
    if (field === 'medicine') {
      console.log("Setting medicine with value:", value);
      // Find the complete medicine object
      const medicine = Array.isArray(medicines) ? medicines.find(m => 
        (m._id && m._id.toString() === value.toString()) || 
        (m.id && m.id.toString() === value.toString())
      ) : null;
      console.log("Found medicine:", medicine);
      
      if (medicine) {
        // Store both the ID and the medicine name for better display
        setNewMedication({
          ...newMedication,
          medicine: value,
          medicineName: medicine.name, // Store the name for display
          medicineDetails: {
            ...medicine,
            // Ensure required fields exist even if API doesn't provide them
            stock: medicine.stock || 0,
            unit: medicine.unit || 'units',
            name: medicine.name
          },
          dosage: {
            morning: false,
            afternoon: false,
            evening: false,
            night: false,
            morningBeforeFood: false,
            morningAfterFood: false,
            afternoonBeforeFood: false,
            afternoonAfterFood: false,
            eveningBeforeFood: false,
            eveningAfterFood: false,
            nightBeforeFood: false,
            nightAfterFood: false
          }
        });
        
        // Update the search field to show the selected medicine name
        setMedicineSearch(medicine.name);
      } else {
        console.warn("Medicine not found for ID:", value);
        setNewMedication({
          ...newMedication,
          medicine: value
        });
      }
    } else if (field.startsWith('dosage.')) {
      const [_, time, type] = field.split('.');
      setNewMedication(prev => {
        const newDosage = { ...prev.dosage };
        
        // Handle time of day checkboxes
        if (time === 'morning' || time === 'afternoon' || time === 'evening' || time === 'night') {
          newDosage[field] = value;
          // Reset before/after food when time is unchecked
          if (!value) {
            newDosage[`${time}BeforeFood`] = false;
            newDosage[`${time}AfterFood`] = false;
          }
        } else {
          // Handle before/after food checkboxes
          newDosage[field] = value;
        }
        
        return {
          ...prev,
          dosage: newDosage
        };
      });
    } else {
      setNewMedication({
        ...newMedication,
        [field]: value
      });
    }
  };

  const addMedication = () => {
    if (!newMedication.medicine) {
      console.log("No medicine selected, can't add");
      return;
    }
    
    console.log("Adding medication with data:", newMedication);

    // Get the complete medicine details if available
    const medicineId = newMedication.medicine;
    let medicineDetails = newMedication.medicineDetails;
    let medicineName = newMedication.medicineName;
    
    // If medicine details aren't already stored, look them up
    if (!medicineDetails || !medicineName) {
      const medicine = Array.isArray(medicines) ? medicines.find(m => 
        (m._id && m._id.toString() === medicineId.toString()) || 
        (m.id && m.id.toString() === medicineId.toString())
      ) : null;
      
      if (medicine) {
        console.log("Found medicine details:", medicine);
        medicineDetails = medicine;
        medicineName = medicine.name;
      } else {
        console.warn("Medicine not found for ID:", medicineId);
      }
    }

    // Create a new medication object with a newly generated temporary ID
    const medicationWithFlag = {
      ...newMedication,
      _id: `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Generate a unique temporary ID
      medicineName: medicineName,
      medicineDetails: medicineDetails
    };

    // Stock system removed - no stock validation

    console.log("Adding medication with enhanced data:", medicationWithFlag);
    console.log("Current medications before adding:", formData.medications);
    const updatedMedications = [...formData.medications, medicationWithFlag];
    console.log("Updated medications after adding:", updatedMedications);
    setFormData({
      ...formData,
      medications: updatedMedications
    });
    console.log("FormData after setting:", { ...formData, medications: updatedMedications });

    // Reset the new medication form
    setNewMedication({
      medicine: '',
      medicineName: '',
      medicineDetails: null,
      dosage: {
        morning: false,
        afternoon: false,
        evening: false,
        night: false,
        morningBeforeFood: false,
        morningAfterFood: false,
        afternoonBeforeFood: false,
        afternoonAfterFood: false,
        eveningBeforeFood: false,
        eveningAfterFood: false,
        nightBeforeFood: false,
        nightAfterFood: false
      },
      duration: '',
      instructions: '',
      providedFromStock: false,
      quantity: 0
    });
    
    // Clear the search field
    setMedicineSearch('');
    
    // Clear any previous errors
    setError(null);
  };

  const removeMedication = index => {
    const updatedMedications = formData.medications.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      medications: updatedMedications
    });
  };

  // Replace handleMedicineSearch with useEffect
  useEffect(() => {
    if (debouncedMedicineSearch.trim() === '') {
      setSearchResults([]);
      return;
    }

    const searchMedicines = async () => {
      try {
        // Fix the search API endpoint to use .php extension
        const res = await axios.get(`/api/medicines.php?search=${encodeURIComponent(debouncedMedicineSearch)}`);
        console.log("Search results:", res.data);
        
        // Check if the response has the medicines property and handle both response formats
        if (res.data && res.data.medicines) {
          setSearchResults(res.data.medicines);
        } else if (Array.isArray(res.data)) {
          setSearchResults(res.data);
        } else {
          setSearchResults([]);
        }
      } catch (err) {
        console.error("Error searching medicines:", err);
        setSearchResults([]);
      }
    };

    searchMedicines();
  }, [debouncedMedicineSearch]);

  // Fetal Biometry Table reference data
  const fetalBiometryTable = {
    // Format: [BPD, HC, AC, FL] in mm
    13: [26.2, 93.6, 67.4, 10.9],
    14: [30.1, 107.8, 78.9, 14.1],
    15: [33.8, 121.9, 90.3, 17.2],
    16: [37.5, 135.7, 101.6, 20.3],
    17: [41.0, 149.3, 112.9, 23.3],
    18: [44.5, 162.7, 124.1, 26.3],
    19: [48.0, 175.7, 135.2, 29.2],
    20: [51.3, 188.6, 146.2, 32.1],
    21: [54.5, 201.1, 157.1, 34.9],
    22: [57.7, 213.3, 168.0, 37.6],
    23: [60.7, 225.3, 178.7, 40.3],
    24: [63.6, 236.9, 189.3, 42.9],
    25: [66.5, 248.1, 199.8, 45.5],
    26: [69.3, 259.0, 210.2, 48.0],
    27: [71.9, 269.5, 220.4, 50.4],
    28: [74.3, 279.6, 230.6, 52.7],
    29: [76.7, 289.4, 240.5, 55.0],
    30: [79.0, 298.7, 250.4, 57.1],
    31: [81.3, 307.6, 260.1, 59.2],
    32: [83.4, 316.0, 269.7, 61.2],
    33: [85.5, 324.0, 279.1, 63.1],
    34: [87.4, 331.5, 288.4, 64.9],
    35: [89.2, 338.5, 297.5, 66.6],
    36: [90.9, 345.0, 306.4, 68.2],
    37: [92.5, 351.0, 315.1, 69.7],
    38: [94.0, 356.5, 323.7, 71.1],
    39: [95.4, 361.4, 332.1, 72.4],
    40: [96.7, 365.8, 340.4, 73.6]
  };

  // Find closest gestational age for given measurement
  const findGestationalAge = (measurement, type) => {
    let closestWeek = 0;
    let smallestDiff = Infinity;
    
    // Determine measurement index in array
    const measurementIndex = {
      'biparietalDiameter': 0,
      'headCircumference': 1,
      'abdominalCircumference': 2,
      'femurLength': 3
    }[type];
    
    if (measurementIndex === undefined) return null;
    
    // Find closest match in table
    for (const week in fetalBiometryTable) {
      const tableValue = fetalBiometryTable[week][measurementIndex];
      const diff = Math.abs(tableValue - measurement);
      
      if (diff < smallestDiff) {
        smallestDiff = diff;
        closestWeek = parseInt(week);
      }
    }
    
    return closestWeek;
  };

  // Obstetric measurement relationships
  const calculateMeasurements = (field, value, currentMeasurements) => {
    // Convert value to number for calculations if possible
    const numValue = parseFloat(value);
    
    // Get clean measurements object
    const measurements = {...currentMeasurements};
    
    // Always update the current field regardless of calculations
    measurements[field] = value;
    
    // Handle gestational age input - only update other fields if explicitly changing GA
    if (field === 'gestationalAge') {
      // Extract the weeks value from input (e.g., "30 weeks" -> 30)
      const weeks = parseInt(value?.split(' ')[0]);
      
      if (!isNaN(weeks) && weeks >= 13 && weeks <= 40) {
        // Look up values from table
        if (fetalBiometryTable[weeks]) {
          // Only update fields that haven't been manually edited
          if (!measurements.manuallyEdited?.includes('biparietalDiameter')) {
            measurements.biparietalDiameter = fetalBiometryTable[weeks][0].toString();
          }
          if (!measurements.manuallyEdited?.includes('headCircumference')) {
            measurements.headCircumference = fetalBiometryTable[weeks][1].toString();
          }
          if (!measurements.manuallyEdited?.includes('abdominalCircumference')) {
            measurements.abdominalCircumference = fetalBiometryTable[weeks][2].toString();
          }
          if (!measurements.manuallyEdited?.includes('femurLength')) {
            measurements.femurLength = fetalBiometryTable[weeks][3].toString();
          }
        }
      }
      return measurements;
    }
    
    // Initialize manuallyEdited array if it doesn't exist
    if (!measurements.manuallyEdited) {
      measurements.manuallyEdited = [];
    }
    
    // Mark this field as manually edited to prevent auto-updates
    if (!measurements.manuallyEdited.includes(field)) {
      measurements.manuallyEdited = [...measurements.manuallyEdited, field];
    }
    
    return measurements;
  };

  // Handle ultrasound report changes
  const handleUltrasoundChange = (field, value) => {
    try {
      // Simple update - no automatic calculations
      setFormData({
        ...formData,
        ultrasoundReport: {
          ...formData.ultrasoundReport,
          [field]: value
        }
      });
    } catch (error) {
      console.error('Error updating ultrasound field:', error);
      setError('Failed to update ultrasound information');
    }
  };

  // Toggle ultrasound report
  const toggleUltrasoundReport = () => {
    try {
      setFormData({
        ...formData,
        ultrasoundReport: {
          ...formData.ultrasoundReport,
          hasUltrasound: !formData.ultrasoundReport.hasUltrasound
        }
      });
    } catch (error) {
      console.error('Error toggling ultrasound report:', error);
      setError('Failed to update ultrasound setting');
    }
  };

  // Render ultrasound form based on type
  const renderUltrasoundForm = () => {
    try {
      const { type } = formData.ultrasoundReport || {};
      
      // Common fields for all ultrasound types
      const commonFields = (
        <>
          <Form.Group className="mb-3">
            <Form.Label>Findings</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={formData.ultrasoundReport?.findings || ''}
              onChange={(e) => handleUltrasoundChange('findings', e.target.value)}
              placeholder="Enter detailed findings"
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Impression</Form.Label>
            <Form.Control
              as="input"
              list="impression-options"
              value={formData.ultrasoundReport?.impression || ''}
              onChange={(e) => handleUltrasoundChange('impression', e.target.value)}
              placeholder="Type your impression or select from list"
            />
            <datalist id="impression-options">
              <option value="Normal pelvic ultrasound" />
              <option value="Polycystic ovarian morphology (PCOS)" />
              <option value="Simple ovarian cyst" />
              <option value="Complex ovarian cyst" />
              <option value="Uterine fibroid (leiomyoma)" />
              <option value="Endometrial thickening" />
              <option value="Endometrial polyp" />
              <option value="Pelvic inflammatory disease (PID)" />
              <option value="Ovarian mass/tumor suspicion" />
              <option value="Adnexal mass" />
              <option value="Hydrosalpinx" />
              <option value="Pyosalpinx" />
              <option value="Free fluid in pelvis" />
              <option value="Post-surgical changes" />
              <option value="Normal prostate and urinary bladder" />
              <option value="Enlarged prostate (BPH)" />
              <option value="Prostatitis" />
              <option value="Bladder wall thickening" />
              <option value="Urinary bladder mass" />
              <option value="Normal abdominal ultrasound" />
              <option value="Hepatomegaly with fatty infiltration" />
              <option value="Cholelithiasis - surgical consultation advised" />
              <option value="Renal calculi - urological follow-up" />
              <option value="Pancreatic mass - urgent CT recommended" />
              <option value="Splenomegaly - hematological workup advised" />
              <option value="Ascites - further evaluation required" />
              <option value="Bowel obstruction - emergency referral" />
              <option value="Abdominal mass - tissue diagnosis needed" />
              <option value="Chronic liver disease - hepatology consultation" />
              <option value="Fatty liver" />
              <option value="Hepatomegaly with fatty changes" />
              <option value="Gallstones" />
              <option value="Features suggestive of cholecystitis" />
              <option value="Renal calculi" />
              <option value="Hydronephrosis" />
              <option value="Splenomegaly" />
              <option value="Ascites" />
              <option value="Mass lesion – further evaluation advised" />
            </datalist>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Recommendations</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.ultrasoundReport?.recommendations || ''}
              onChange={(e) => handleUltrasoundChange('recommendations', e.target.value)}
              placeholder="Enter recommendations if any"
            />
          </Form.Group>
          
          <Form.Group className="mb-4">
            <Form.Label>Ultrasound Images</Form.Label>
            <div className="mb-3">
              <div className="input-group mb-2">
                <input
                  type="file"
                  className="form-control"
                  onChange={handleUltrasoundImageUpload}
                  accept="image/*"
                  id="ultrasound-image-input"
                />
                <Button 
                  variant="primary" 
                  onClick={uploadSelectedImage}
                  disabled={!selectedImage || uploadingImage}
                >
                  {uploadingImage ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" /> Uploading...
                    </>
                  ) : (
                    <>Upload Image</>
                  )}
                </Button>
              </div>
              <small className="text-muted">
                You can upload multiple images by selecting and uploading them one at a time.
              </small>
            </div>
            
            {formData.ultrasoundReport.images && formData.ultrasoundReport.images.length > 0 && (
              <div className="ultrasound-images-gallery mb-3">
                <h6>Uploaded Images:</h6>
                <table style={{ width: '100%', border: 'none', borderCollapse: 'collapse' }}>
                  <tbody>
                    {Array.from({ length: Math.ceil(formData.ultrasoundReport.images.length / 2) }).map((_, rowIndex) => (
                      <tr key={rowIndex}>
                        {/* First image in pair */}
                        {formData.ultrasoundReport.images[rowIndex * 2] && (
                          <td style={{ width: '50%', padding: '8px', verticalAlign: 'top' }}>
                            <Card>
                              <Card.Img 
                                variant="top" 
                                src={window.location.origin + formData.ultrasoundReport.images[rowIndex * 2].path} 
                                alt={`Ultrasound image ${rowIndex * 2 + 1}`}
                                style={{ height: formData.ultrasoundReport.images[rowIndex * 2].height + 'px', objectFit: 'contain' }}
                              />
                              <Card.Body className="p-2">
                                <Form.Control
                                  size="sm"
                                  type="text"
                                  placeholder="Add caption"
                                  value={formData.ultrasoundReport.images[rowIndex * 2].caption || ''}
                                  onChange={(e) => handleImageCaptionChange(rowIndex * 2, e.target.value)}
                                  className="mb-2"
                                />
                                <div className="d-flex justify-content-between">
                                  <Form.Group className="me-1" style={{ width: '45%' }}>
                                    <Form.Label className="small">Width</Form.Label>
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      min="100"
                                      max="800"
                                      value={formData.ultrasoundReport.images[rowIndex * 2].width || 300}
                                      onChange={(e) => handleImageSizeChange(rowIndex * 2, 'width', parseInt(e.target.value))}
                                    />
                                  </Form.Group>
                                  <Form.Group style={{ width: '45%' }}>
                                    <Form.Label className="small">Height</Form.Label>
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      min="100"
                                      max="600"
                                      value={formData.ultrasoundReport.images[rowIndex * 2].height || 200}
                                      onChange={(e) => handleImageSizeChange(rowIndex * 2, 'height', parseInt(e.target.value))}
                                    />
                                  </Form.Group>
                                </div>
                                <Button 
                                  variant="outline-danger" 
                                  size="sm" 
                                  className="mt-2 w-100"
                                  onClick={() => removeUltrasoundImage(rowIndex * 2)}
                                >
                                  <FaTrash className="me-1" /> Remove
                                </Button>
                              </Card.Body>
                            </Card>
                          </td>
                        )}
                        
                        {/* Second image in pair */}
                        {formData.ultrasoundReport.images[rowIndex * 2 + 1] && (
                          <td style={{ width: '50%', padding: '8px', verticalAlign: 'top' }}>
                            <Card>
                              <Card.Img 
                                variant="top" 
                                src={window.location.origin + formData.ultrasoundReport.images[rowIndex * 2 + 1].path} 
                                alt={`Ultrasound image ${rowIndex * 2 + 2}`}
                                style={{ height: formData.ultrasoundReport.images[rowIndex * 2 + 1].height + 'px', objectFit: 'contain' }}
                              />
                              <Card.Body className="p-2">
                                <Form.Control
                                  size="sm"
                                  type="text"
                                  placeholder="Add caption"
                                  value={formData.ultrasoundReport.images[rowIndex * 2 + 1].caption || ''}
                                  onChange={(e) => handleImageCaptionChange(rowIndex * 2 + 1, e.target.value)}
                                  className="mb-2"
                                />
                                <div className="d-flex justify-content-between">
                                  <Form.Group className="me-1" style={{ width: '45%' }}>
                                    <Form.Label className="small">Width</Form.Label>
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      min="100"
                                      max="800"
                                      value={formData.ultrasoundReport.images[rowIndex * 2 + 1].width || 300}
                                      onChange={(e) => handleImageSizeChange(rowIndex * 2 + 1, 'width', parseInt(e.target.value))}
                                    />
                                  </Form.Group>
                                  <Form.Group style={{ width: '45%' }}>
                                    <Form.Label className="small">Height</Form.Label>
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      min="100"
                                      max="600"
                                      value={formData.ultrasoundReport.images[rowIndex * 2 + 1].height || 200}
                                      onChange={(e) => handleImageSizeChange(rowIndex * 2 + 1, 'height', parseInt(e.target.value))}
                                    />
                                  </Form.Group>
                                </div>
                                <Button 
                                  variant="outline-danger" 
                                  size="sm" 
                                  className="mt-2 w-100"
                                  onClick={() => removeUltrasoundImage(rowIndex * 2 + 1)}
                                >
                                  <FaTrash className="me-1" /> Remove
                                </Button>
                              </Card.Body>
                            </Card>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Form.Group>
        </>
      );
      
      // Additional fields for Obstetric ultrasound
      if (type === 'Obstetric') {
        return (
          <>
            {commonFields}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Gestational Age</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.ultrasoundReport?.gestationalAge || ''}
                    onChange={(e) => handleUltrasoundChange('gestationalAge', e.target.value)}
                    placeholder="e.g., 28 weeks 3 days"
                  />
                  <Button 
                    variant="outline-success" 
                    size="sm" 
                    className="mt-1"
                    disabled={calculatingMeasurements}
                    onClick={() => {
                      const gaValue = formData.ultrasoundReport?.gestationalAge;
                      if (gaValue) {
                        calculateAIMeasurements('gestationalAge', gaValue);
                      }
                    }}
                  >
                    {calculatingMeasurements ? '⏳ AI Calculating...' : '🤖 AI Fill All From GA'}
                  </Button>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fetal Heart Rate</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      value={formData.ultrasoundReport?.fetalHeartRate || ''}
                      onChange={(e) => handleUltrasoundChange('fetalHeartRate', e.target.value)}
                      placeholder="e.g., 140"
                    />
                    <InputGroup.Text>bpm</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Gestational Sac (GS)</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      value={formData.ultrasoundReport?.gestationalSac || ''}
                      onChange={(e) => handleUltrasoundChange('gestationalSac', e.target.value)}
                      placeholder="e.g., 32"
                    />
                    <InputGroup.Text>mm</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Crown-Rump Length (CRL)</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      value={formData.ultrasoundReport?.crownRumpLength || ''}
                      onChange={(e) => handleUltrasoundChange('crownRumpLength', e.target.value)}
                      placeholder="e.g., 20"
                    />
                    <InputGroup.Text>mm</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Cardiac Flicker</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.ultrasoundReport?.cardiacFlicker || ''}
                    onChange={(e) => handleUltrasoundChange('cardiacFlicker', e.target.value)}
                    placeholder="e.g., Present/Absent"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Biparietal Diameter (BPD)</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      value={formData.ultrasoundReport?.biparietalDiameter || ''}
                      onChange={(e) => handleUltrasoundChange('biparietalDiameter', e.target.value)}
                      placeholder="e.g., 72"
                    />
                    <InputGroup.Text>mm</InputGroup.Text>
                  </InputGroup>
                  <Button 
                    variant="outline-success" 
                    size="sm" 
                    className="mt-1"
                    disabled={calculatingMeasurements}
                    onClick={() => {
                      const bpdValue = formData.ultrasoundReport?.biparietalDiameter;
                      if (bpdValue) {
                        calculateAIMeasurements('biparietalDiameter', bpdValue);
                      }
                    }}
                  >
                    {calculatingMeasurements ? '⏳ AI Calculating...' : '🤖 AI Fill All From BPD'}
                  </Button>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Head Circumference (HC)</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      value={formData.ultrasoundReport?.headCircumference || ''}
                      onChange={(e) => handleUltrasoundChange('headCircumference', e.target.value)}
                      placeholder="e.g., 260"
                    />
                    <InputGroup.Text>mm</InputGroup.Text>
                  </InputGroup>
                  <Button 
                    variant="outline-success" 
                    size="sm" 
                    className="mt-1"
                    disabled={calculatingMeasurements}
                    onClick={() => {
                      const hcValue = formData.ultrasoundReport?.headCircumference;
                      if (hcValue) {
                        calculateAIMeasurements('headCircumference', hcValue);
                      }
                    }}
                  >
                    {calculatingMeasurements ? '⏳ AI Calculating...' : '🤖 AI Fill All From HC'}
                  </Button>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Abdominal Circumference (AC)</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      value={formData.ultrasoundReport?.abdominalCircumference || ''}
                      onChange={(e) => handleUltrasoundChange('abdominalCircumference', e.target.value)}
                      placeholder="e.g., 240"
                    />
                    <InputGroup.Text>mm</InputGroup.Text>
                  </InputGroup>
                  <Button 
                    variant="outline-success" 
                    size="sm" 
                    className="mt-1"
                    disabled={calculatingMeasurements}
                    onClick={() => {
                      const acValue = formData.ultrasoundReport?.abdominalCircumference;
                      if (acValue) {
                        calculateAIMeasurements('abdominalCircumference', acValue);
                      }
                    }}
                  >
                    {calculatingMeasurements ? '⏳ AI Calculating...' : '🤖 AI Fill All From AC'}
                  </Button>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Femur Length (FL)</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="text"
                      value={formData.ultrasoundReport?.femurLength || ''}
                      onChange={(e) => handleUltrasoundChange('femurLength', e.target.value)}
                      placeholder="e.g., 55"
                    />
                    <InputGroup.Text>mm</InputGroup.Text>
                  </InputGroup>
                  <Button 
                    variant="outline-success" 
                    size="sm" 
                    className="mt-1"
                    disabled={calculatingMeasurements}
                    onClick={() => {
                      const flValue = formData.ultrasoundReport?.femurLength;
                      if (flValue) {
                        calculateAIMeasurements('femurLength', flValue);
                      }
                    }}
                  >
                    {calculatingMeasurements ? '⏳ AI Calculating...' : '🤖 AI Fill All From FL'}
                  </Button>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fetal Presentation</Form.Label>
                  <Form.Select
                    value={formData.ultrasoundReport?.fetalPresentation || ''}
                    onChange={(e) => handleUltrasoundChange('fetalPresentation', e.target.value)}
                  >
                    <option value="">Select Presentation</option>
                    <option value="Cephalic">Cephalic</option>
                    <option value="Breech">Breech</option>
                    <option value="Transverse">Transverse</option>
                    <option value="Oblique">Oblique</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fetal Lie</Form.Label>
                  <Form.Select
                    value={formData.ultrasoundReport?.fetalLie || ''}
                    onChange={(e) => handleUltrasoundChange('fetalLie', e.target.value)}
                  >
                    <option value="">Select Lie</option>
                    <option value="Longitudinal">Longitudinal</option>
                    <option value="Transverse">Transverse</option>
                    <option value="Oblique">Oblique</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fetal Movement</Form.Label>
                  <Form.Select
                    value={formData.ultrasoundReport?.fetalMovement || ''}
                    onChange={(e) => handleUltrasoundChange('fetalMovement', e.target.value)}
                  >
                    <option value="">Select Movement</option>
                    <option value="Normal">Normal</option>
                    <option value="Active/Fast">Active/Fast</option>
                    <option value="Reduced/Slow">Reduced/Slow</option>
                    <option value="Absent">Absent</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cardiac Activity</Form.Label>
                  <Form.Select
                    value={formData.ultrasoundReport?.cardiacActivity || ''}
                    onChange={(e) => handleUltrasoundChange('cardiacActivity', e.target.value)}
                  >
                    <option value="">Select Activity</option>
                    <option value="Present">Present</option>
                    <option value="Absent">Absent</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Placenta Position</Form.Label>
                  <Form.Select
                    value={formData.ultrasoundReport?.placentaPosition || ''}
                    onChange={(e) => handleUltrasoundChange('placentaPosition', e.target.value)}
                  >
                    <option value="">Select Position</option>
                    <option value="Anterior">Anterior</option>
                    <option value="Posterior">Posterior</option>
                    <option value="Fundal">Fundal</option>
                    <option value="Low lying">Low lying</option>
                    <option value="Placenta previa">Placenta previa</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Amniotic Fluid</Form.Label>
                  <Form.Select
                    value={formData.ultrasoundReport?.amniotic || ''}
                    onChange={(e) => handleUltrasoundChange('amniotic', e.target.value)}
                  >
                    <option value="">Select Fluid Level</option>
                    <option value="Normal">Normal</option>
                    <option value="Oligohydramnios">Oligohydramnios</option>
                    <option value="Polyhydramnios">Polyhydramnios</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Button 
              variant="success" 
              className="mt-2 mb-4" 
              onClick={generateAIReport}
              disabled={generatingReport}
            >
              {generatingReport ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  AI Generating Report...
                </>
              ) : (
                <>
                  🤖 AI Generate Report & Advice
                </>
              )}
            </Button>
          </>
        );
      }
      
      // For Abdominal ultrasound
      if (type === 'Abdominal') {
        return (
          <>
            {commonFields}
            <h6 className="mt-4 mb-3">Organ-Specific Findings</h6>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Liver</Form.Label>
                  <Form.Select
                    value={formData.ultrasoundReport?.liver || ''}
                    onChange={(e) => handleUltrasoundChange('liver', e.target.value)}
                  >
                    <option value="">Select Liver Finding</option>
                    <option value="Normal size and echotexture">Normal size and echotexture</option>
                    <option value="Fatty liver (mild)">Fatty liver (mild)</option>
                    <option value="Fatty liver (moderate)">Fatty liver (moderate)</option>
                    <option value="Fatty liver (severe)">Fatty liver (severe)</option>
                    <option value="Hepatomegaly">Hepatomegaly</option>
                    <option value="Cirrhotic changes">Cirrhotic changes</option>
                    <option value="Focal lesion (cyst)">Focal lesion (cyst)</option>
                    <option value="Focal lesion (hemangioma)">Focal lesion (hemangioma)</option>
                    <option value="Focal lesion (mass)">Focal lesion (mass)</option>
                    <option value="Intrahepatic biliary dilatation">Intrahepatic biliary dilatation</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Gallbladder & Biliary Tree</Form.Label>
                  <Form.Select
                    value={formData.ultrasoundReport?.gallbladder || ''}
                    onChange={(e) => handleUltrasoundChange('gallbladder', e.target.value)}
                  >
                    <option value="">Select Gallbladder Finding</option>
                    <option value="Normal">Normal</option>
                    <option value="Cholelithiasis (gallstones)">Cholelithiasis (gallstones)</option>
                    <option value="Gallbladder sludge">Gallbladder sludge</option>
                    <option value="Cholecystitis (acute)">Cholecystitis (acute)</option>
                    <option value="Cholecystitis (chronic)">Cholecystitis (chronic)</option>
                    <option value="Polyps">Polyps</option>
                    <option value="CBD dilatation">CBD dilatation</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Pancreas</Form.Label>
                  <Form.Select
                    value={formData.ultrasoundReport?.pancreas || ''}
                    onChange={(e) => handleUltrasoundChange('pancreas', e.target.value)}
                  >
                    <option value="">Select Pancreas Finding</option>
                    <option value="Normal">Normal</option>
                    <option value="Pancreatitis (acute)">Pancreatitis (acute)</option>
                    <option value="Pancreatitis (chronic)">Pancreatitis (chronic)</option>
                    <option value="Mass lesion">Mass lesion</option>
                    <option value="Cystic lesion">Cystic lesion</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Spleen</Form.Label>
                  <Form.Select
                    value={formData.ultrasoundReport?.spleen || ''}
                    onChange={(e) => handleUltrasoundChange('spleen', e.target.value)}
                  >
                    <option value="">Select Spleen Finding</option>
                    <option value="Normal size and echotexture">Normal size and echotexture</option>
                    <option value="Splenomegaly">Splenomegaly</option>
                    <option value="Splenic lesion / cyst">Splenic lesion / cyst</option>
                    <option value="Post-splenectomy status">Post-splenectomy status</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Kidneys & Urinary Tract</Form.Label>
                  <Form.Select
                    value={formData.ultrasoundReport?.kidneys || ''}
                    onChange={(e) => handleUltrasoundChange('kidneys', e.target.value)}
                  >
                    <option value="">Select Kidney Finding</option>
                    <option value="Normal size, shape, echotexture">Normal size, shape, echotexture</option>
                    <option value="Hydronephrosis (mild)">Hydronephrosis (mild)</option>
                    <option value="Hydronephrosis (moderate)">Hydronephrosis (moderate)</option>
                    <option value="Hydronephrosis (severe)">Hydronephrosis (severe)</option>
                    <option value="Renal calculi">Renal calculi</option>
                    <option value="Renal cysts">Renal cysts</option>
                    <option value="Chronic kidney disease changes">Chronic kidney disease changes</option>
                    <option value="Mass lesion">Mass lesion</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Other Structures</Form.Label>
                  <Form.Select
                    value={formData.ultrasoundReport?.otherStructures || ''}
                    onChange={(e) => handleUltrasoundChange('otherStructures', e.target.value)}
                  >
                    <option value="">Select Other Finding</option>
                    <option value="Normal aorta & IVC">Normal aorta & IVC</option>
                    <option value="Lymphadenopathy">Lymphadenopathy</option>
                    <option value="Ascites">Ascites</option>
                    <option value="Free fluid in peritoneal cavity">Free fluid in peritoneal cavity</option>
                    <option value="Bowel loops – normal">Bowel loops – normal</option>
                    <option value="Bowel loops – dilated">Bowel loops – dilated</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </>
        );
      }
      
      // For other types, return common fields
      return commonFields;
    } catch (error) {
      console.error('Error rendering ultrasound form:', error);
      return (
        <Alert variant="danger">
          Error loading ultrasound form. Please try toggling the ultrasound option.
        </Alert>
      );
    }
  };

  // Add image handling functions
  // Handle ultrasound image upload input change
  const handleUltrasoundImageUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedImage(e.target.files[0]);
    }
  };

  // Upload the selected image to server
  const uploadSelectedImage = async () => {
    if (!selectedImage) return;
    
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('ultrasoundImage', selectedImage);
    
    try {
      const res = await axios.post('/api/uploads/ultrasound-image.php', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Add uploaded image to the report's ultrasound images
      const newImage = {
        path: res.data.path,
        width: 300,
        height: 200,
        caption: ''
      };
      
      setFormData(prevState => ({
        ...prevState,
        ultrasoundReport: {
          ...prevState.ultrasoundReport,
          images: [...(prevState.ultrasoundReport.images || []), newImage]
        }
      }));
      
      // Reset selected image
      setSelectedImage(null);
      setUploadingImage(false);
      
      // Reset the file input by clearing its value
      document.getElementById('ultrasound-image-input').value = '';
      
    } catch (err) {
      console.error('Error uploading ultrasound image:', err);
      setError('Failed to upload image: ' + (err.response?.data?.msg || err.message));
      setUploadingImage(false);
    }
  };

  // Handle image caption change
  const handleImageCaptionChange = (index, caption) => {
    setFormData(prevState => {
      const updatedImages = [...(prevState.ultrasoundReport.images || [])];
      updatedImages[index] = { ...updatedImages[index], caption };
      
      return {
        ...prevState,
        ultrasoundReport: {
          ...prevState.ultrasoundReport,
          images: updatedImages
        }
      };
    });
  };

  // Handle image size change
  const handleImageSizeChange = (index, dimension, value) => {
    setFormData(prevState => {
      const updatedImages = [...(prevState.ultrasoundReport.images || [])];
      updatedImages[index] = { ...updatedImages[index], [dimension]: value };
      
      return {
        ...prevState,
        ultrasoundReport: {
          ...prevState.ultrasoundReport,
          images: updatedImages
        }
      };
    });
  };

  // Remove ultrasound image
  const removeUltrasoundImage = (index) => {
    setFormData(prevState => {
      const updatedImages = [...(prevState.ultrasoundReport.images || [])];
      updatedImages.splice(index, 1);
      
      return {
        ...prevState,
        ultrasoundReport: {
          ...prevState.ultrasoundReport,
          images: updatedImages
        }
      };
    });
  };

  // Helper function to format dosage information
  const formatDosage = (dosage) => {
    const timings = [];
    if (dosage.morning) {
      timings.push("Morning" + (dosage.morningBeforeFood && dosage.morningAfterFood 
        ? " (before & after food)" 
        : dosage.morningBeforeFood 
          ? " (before food)" 
          : dosage.morningAfterFood 
            ? " (after food)" 
            : ""));
    }
    if (dosage.afternoon) {
      timings.push("Afternoon" + (dosage.afternoonBeforeFood && dosage.afternoonAfterFood 
        ? " (before & after food)" 
        : dosage.afternoonBeforeFood 
          ? " (before food)" 
          : dosage.afternoonAfterFood 
            ? " (after food)" 
            : ""));
    }
    if (dosage.evening) {
      timings.push("Evening" + (dosage.eveningBeforeFood && dosage.eveningAfterFood 
        ? " (before & after food)" 
        : dosage.eveningBeforeFood 
          ? " (before food)" 
          : dosage.eveningAfterFood 
            ? " (after food)" 
            : ""));
    }
    if (dosage.night) {
      timings.push("Night" + (dosage.nightBeforeFood && dosage.nightAfterFood 
        ? " (before & after food)" 
        : dosage.nightBeforeFood 
          ? " (before food)" 
          : dosage.nightAfterFood 
            ? " (after food)" 
            : ""));
    }
    return timings.join(", ");
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Validate form
      if (!formData.doctor || !formData.diagnosis) {
        setError('Please select a doctor and provide a diagnosis');
        setSaving(false);
        return;
      }

      console.log("=== SUBMIT DEBUG ===");
      console.log("Form data:", formData);
      console.log("Patient ID from params:", patientId);
      console.log("Patient ID from formData:", formData.patient);
      console.log("Patient object:", patient);
      console.log("Patient internal ID:", patient?.id);
      console.log("Is edit mode:", isEditMode);

      // CRITICAL FIX: Always use patient.id (internal database ID)
      // Never use patientId from URL params as it might be a string code
      const finalPatientId = patient?.id || formData.patient;
      
      console.log("Final patient ID to send:", finalPatientId, "Type:", typeof finalPatientId);
      
      const reportData = {
        ...formData,
        patientId: finalPatientId,
        // In edit mode, rename 'doctor' to 'doctorId' for backend
        doctorId: formData.doctor,
        id: id // Add report ID for edit mode
      };
      
      // Remove the 'doctor' field as backend expects 'doctorId'
      if (reportData.doctor) {
        delete reportData.doctor;
      }
      
      // Validate and clean date fields
      if (reportData.followUpDate && reportData.followUpDate.trim() === '') {
        delete reportData.followUpDate;
      }
      
      // Ensure date is in proper format or null
      if (reportData.followUpDate) {
        const dateObj = new Date(reportData.followUpDate);
        if (isNaN(dateObj.getTime())) {
          delete reportData.followUpDate;
        }
      }
      
      // Clean up medication data to prevent circular references and ensure proper format
      if (reportData.medications && reportData.medications.length > 0) {
        reportData.medications = reportData.medications.map(med => {
          // Create a clean copy without circular references
          const cleanMed = { ...med };
          
          // Ensure medicine ID is valid and in the correct format
          if (!cleanMed.medicine) {
            throw new Error('Invalid medicine selected');
          }
          
          // Remove medicineDetails to avoid circular references
          if (cleanMed.medicineDetails) {
            delete cleanMed.medicineDetails;
          }
          
          // Ensure quantity is a number
          if (cleanMed.providedFromStock && cleanMed.quantity) {
            cleanMed.quantity = Number(cleanMed.quantity);
          }
          
          return cleanMed;
        });
      }
      
      console.log("Prepared report data:", reportData);

      // Ensure medications have valid data
      if (reportData.medications.length > 0) {
        reportData.medications = reportData.medications.map(med => {
          // Ensure medicine ID is valid
          if (!med.medicine) {
            throw new Error('Invalid medicine selected');
          }
          return med;
        });
      }

      // Stock system removed - no validation needed

      console.log('Submitting report data:', reportData);
      
      let res;
      if (isEditMode) {
        res = await axios.put(`/api/reports.php?id=${id}`, reportData);
        setToastMessage('Report updated successfully!');
      } else {
        res = await axios.post('/api/reports.php', reportData);
        setToastMessage('Report created successfully!');
      }
      
      setSaving(false);
      setToastType('success');
      setShowToast(true);
      
      setTimeout(() => {
        // In edit mode, use the existing report ID from URL
        // In create mode, use the ID returned from backend
        const reportId = isEditMode ? id : (res.data.id || res.data.reportId);
        navigate(`/reports/${reportId}`);
      }, 1500);
    } catch (err) {
      console.error('=== ERROR CREATING REPORT ===');
      console.error('Full error object:', err);
      console.error('Error response:', err.response);
      console.error('Error response data:', err.response?.data);
      console.error('Error message:', err.message);
      
      // Handle different types of errors with more specific messages
      let errorMessage = 'Failed to create report. Please try again.';
      
      if (err.response) {
        // The server responded with an error
        if (err.response.data) {
          // Check for both 'msg' and 'error' fields
          errorMessage = err.response.data.msg || err.response.data.error || JSON.stringify(err.response.data);
          
          // Special handling for validation errors
          if (errorMessage.includes('Validation error')) {
            errorMessage = errorMessage.replace('Validation error:', 'Please fix the following:');
          }
        } else if (err.response.status === 400) {
          errorMessage = 'The report data is invalid. Please check all fields.';
        } else if (err.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (err.message) {
        // Client-side error (e.g., network error)
        errorMessage = err.message;
      }
      
      console.error('Final error message to display:', errorMessage);
      setError(errorMessage);
      setSaving(false);
      
      // Scroll to the top to show the error message
      window.scrollTo(0, 0);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => reportRef.current,
    onBeforeGetContent: () => {
      // Debug: Check if ultrasound report exists
      console.log('=== PRINT DEBUG ===');
      console.log('Has Ultrasound:', formData.ultrasoundReport?.hasUltrasound);
      console.log('Ultrasound Type:', formData.ultrasoundReport?.type);
      console.log('Ultrasound Data:', formData.ultrasoundReport);
      console.log('Bill Number:', formData.billNumber);
      console.log('Form Data:', formData);
      
      // Set a custom page title for the print job
      const originalTitle = document.title;
      document.title = `${patient?.name || 'Patient'} - Medical Report`;
      
      return new Promise(resolve => {
        setSaving(true);
        setTimeout(() => {
          setSaving(false);
          resolve();
        }, 500);
      });
    },
    onAfterPrint: () => {
      // Restore the original page title
      document.title = "Clinic Management";
    },
    pageStyle: '@page { size: A4; margin: 20mm 10mm 25mm 10mm; }',
    removeAfterPrint: true,
    suppressErrors: true
  });

  // Generate report findings based on selected measurements and type
  const generateReportFindings = () => {
    const ultrasound = formData.ultrasoundReport;
    const { type } = ultrasound;
    
    let findings = '';
    
    if (type === 'Obstetric') {
      findings = `Obstetric ultrasound examination performed.\n\n`;
      
      if (ultrasound.gestationalAge) {
        findings += `Gestational age: ${ultrasound.gestationalAge}\n`;
      }
      
      if (ultrasound.biparietalDiameter || ultrasound.headCircumference || ultrasound.abdominalCircumference || ultrasound.femurLength) {
        findings += `\nFetal biometry:\n`;
        if (ultrasound.biparietalDiameter) findings += `- BPD: ${ultrasound.biparietalDiameter} mm\n`;
        if (ultrasound.headCircumference) findings += `- HC: ${ultrasound.headCircumference} mm\n`;
        if (ultrasound.abdominalCircumference) findings += `- AC: ${ultrasound.abdominalCircumference} mm\n`;
        if (ultrasound.femurLength) findings += `- FL: ${ultrasound.femurLength} mm\n`;
      }
      
      if (ultrasound.fetalHeartRate) {
        const fhr = parseFloat(ultrasound.fetalHeartRate);
        if (fhr === 0) {
          findings += `\nFetal heart rate: Not detected\nCardiac activity: Absent\n`;
        } else {
          findings += `\nFetal heart rate: ${ultrasound.fetalHeartRate} bpm\n`;
        }
      }
      
      if (ultrasound.placentaPosition) {
        findings += `Placenta: ${ultrasound.placentaPosition}\n`;
      }
      
      if (ultrasound.amniotic) {
        findings += `Amniotic fluid: ${ultrasound.amniotic}\n`;
      }
    } else if (type === 'Abdominal') {
      findings = `Abdominal ultrasound examination performed.\n\n`;
      
      findings += `Organ-specific findings:\n`;
      if (ultrasound.liver) findings += `Liver: ${ultrasound.liver}\n`;
      if (ultrasound.gallbladder) findings += `Gallbladder: ${ultrasound.gallbladder}\n`;
      if (ultrasound.pancreas) findings += `Pancreas: ${ultrasound.pancreas}\n`;
      if (ultrasound.spleen) findings += `Spleen: ${ultrasound.spleen}\n`;
      if (ultrasound.kidneys) findings += `Kidneys: ${ultrasound.kidneys}\n`;
      if (ultrasound.otherStructures) findings += `Other structures: ${ultrasound.otherStructures}\n`;
    } else {
      findings = `${type} ultrasound examination performed.\n\n`;
    }
    
    return findings;
  };
  
  // Generate impression based on selected data
  const generateImpression = () => {
    const ultrasound = formData.ultrasoundReport;
    const { type } = ultrasound;
    
    // If impression is already manually set, keep it
    if (ultrasound.impression && ultrasound.impression.trim() !== '') {
      return ultrasound.impression;
    }
    
    let impression = '';
    
    if (type === 'Obstetric') {
      // Check for zero or absent fetal heart rate - critical condition
      const fhr = parseFloat(ultrasound.fetalHeartRate);
      if (fhr === 0 || ultrasound.cardiacActivity === 'Absent') {
        impression = 'Missed abortion / Fetal demise - Urgent obstetric consultation required';
        return impression;
      }
      
      const gaWeeks = parseInt(ultrasound.gestationalAge?.split(' ')[0]) || 0;
      
      if (gaWeeks) {
        if (gaWeeks < 14) {
          impression = 'Early pregnancy with gestational age consistent with dates';
        } else if (gaWeeks >= 14 && gaWeeks <= 28) {
          impression = 'Second trimester pregnancy with normal fetal anatomy and growth';
        } else {
          impression = 'Third trimester pregnancy with normal fetal growth parameters';
        }
      } else {
        impression = 'Pregnancy with normal fetal parameters';
      }
    } else if (type === 'Abdominal') {
      // Generate impression based on organ findings
      const abnormalFindings = [];
      if (ultrasound.liver && !ultrasound.liver.includes('Normal')) abnormalFindings.push('liver changes');
      if (ultrasound.gallbladder && !ultrasound.gallbladder.includes('Normal')) abnormalFindings.push('gallbladder abnormality');
      if (ultrasound.pancreas && !ultrasound.pancreas.includes('Normal')) abnormalFindings.push('pancreatic changes');
      if (ultrasound.spleen && !ultrasound.spleen.includes('Normal')) abnormalFindings.push('splenic abnormality');
      if (ultrasound.kidneys && !ultrasound.kidneys.includes('Normal')) abnormalFindings.push('renal changes');
      
      if (abnormalFindings.length > 0) {
        impression = `Abdominal ultrasound showing ${abnormalFindings.join(', ')}`;
      } else {
        impression = 'Normal abdominal ultrasound';
      }
    } else {
      impression = `Normal ${type.toLowerCase()} ultrasound`;
    }
    
    return impression;
  };
  
  // Generate doctor's advice based on measurements
  const generateDoctorAdvice = () => {
    const {
      gestationalAge,
      fetalHeartRate,
      cardiacActivity
    } = formData.ultrasoundReport;
    
    // Check for zero or absent fetal heart rate - critical condition
    const fhr = parseFloat(fetalHeartRate);
    if (fhr === 0 || cardiacActivity === 'Absent') {
      return '1. URGENT: Immediate obstetric consultation required\n2. Confirm findings with repeat ultrasound\n3. Discuss management options with patient\n4. Psychological support and counseling\n5. Follow hospital protocol for missed abortion/fetal demise';
    }
    
    // Extract weeks from gestational age
    const gaWeeks = parseInt(gestationalAge?.split(' ')[0]) || 0;
    
    let advice = '';
    
    if (gaWeeks) {
      if (gaWeeks < 12) {
        advice = '1. Continue prenatal vitamins\n2. Maintain adequate hydration and nutrition\n3. Follow-up ultrasound at 12 weeks for NT scan\n4. Schedule routine antenatal check in 4 weeks';
      } else if (gaWeeks >= 12 && gaWeeks < 20) {
        advice = '1. Continue prenatal vitamins\n2. Schedule anomaly scan at 18-20 weeks\n3. Regular antenatal check-ups every 4 weeks\n4. Routine blood tests as per protocol';
      } else if (gaWeeks >= 20 && gaWeeks < 28) {
        advice = '1. Continue prenatal vitamins\n2. Glucose tolerance test (GTT) if not already done\n3. Monitor fetal movements\n4. Regular antenatal check-ups every 4 weeks';
      } else if (gaWeeks >= 28 && gaWeeks < 36) {
        advice = '1. Continue prenatal vitamins\n2. Monitor fetal movements daily\n3. Regular antenatal check-ups every 2 weeks\n4. Prepare for delivery\n5. Signs of labor to watch for';
      } else {
        advice = '1. Continue prenatal vitamins\n2. Monitor fetal movements daily\n3. Weekly antenatal check-ups\n4. Discuss birth plan\n5. Signs of labor to watch for\n6. Schedule follow-up ultrasound if needed';
      }
    } else {
      advice = '1. Continue prenatal vitamins\n2. Regular antenatal check-ups\n3. Further investigation as needed';
    }
    
    return advice;
  };
  
  // Generate recommendations based on measurements
  const generateRecommendations = () => {
    // Get doctor's advice and add it to the recommendations
    const advice = generateDoctorAdvice();
    
    let recommendations = 'Based on current ultrasound findings, the following is recommended:\n\n';
    recommendations += advice;
    
    return recommendations;
  };
  
  // AI-powered measurement calculation
  const [calculatingMeasurements, setCalculatingMeasurements] = useState(false);
  
  const calculateAIMeasurements = async (measurementType, value) => {
    if (!value || value.trim() === '') return;
    
    setCalculatingMeasurements(true);
    
    try {
      const response = await axios.post('/api/calculate-fetal-measurements.php', {
        measurement: measurementType,
        value: value
      });
      
      if (response.data.success) {
        const measurements = response.data.measurements;
        
        setFormData({
          ...formData,
          ultrasoundReport: {
            ...formData.ultrasoundReport,
            gestationalAge: measurements.gestationalAge || '',
            biparietalDiameter: measurements.bpd?.toString() || '',
            headCircumference: measurements.hc?.toString() || '',
            abdominalCircumference: measurements.ac?.toString() || '',
            femurLength: measurements.fl?.toString() || ''
          }
        });
      }
    } catch (err) {
      console.error('AI Calculation Error:', err);
    } finally {
      setCalculatingMeasurements(false);
    }
  };
  
  // AI-powered report generation
  const [generatingReport, setGeneratingReport] = useState(false);
  
  const generateAIReport = async () => {
    setGeneratingReport(true);
    setError(null);
    
    try {
      const response = await axios.post('/api/generate-ultrasound-report.php', {
        ultrasoundData: formData.ultrasoundReport,
        type: formData.ultrasoundReport.type
      });
      
      if (response.data.success) {
        // Update form with AI-generated content
        setFormData({
          ...formData,
          ultrasoundReport: {
            ...formData.ultrasoundReport,
            findings: response.data.findings,
            impression: response.data.impression,
            recommendations: response.data.recommendations
          },
          doctorAdvice: response.data.recommendations // Also set as doctor's advice
        });
        
        setToastMessage('✅ AI Report generated successfully!');
        setToastType('success');
        setShowToast(true);
      }
    } catch (err) {
      console.error('AI Generation Error:', err);
      setError('Failed to generate AI report: ' + (err.response?.data?.error || err.message));
    } finally {
      setGeneratingReport(false);
    }
  };
  
  // Function to auto-fill report sections based on selected data
  const autoFillReportSections = () => {
    const findings = generateReportFindings();
    const impression = generateImpression();
    const recommendations = generateRecommendations();
    const advice = generateDoctorAdvice();
    
    setFormData({
      ...formData,
      ultrasoundReport: {
        ...formData.ultrasoundReport,
        findings,
        impression,
        recommendations
      },
      doctorAdvice: advice
    });
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading patient data...</p>
      </div>
    );
  }

  if (!patient) {
    return (
      <Alert variant="danger">
        Patient not found. <Link to="/patients">Go back to patients list</Link>
      </Alert>
    );
  }

  return (
    <div>
      <Toast 
        show={showToast} 
        onClose={() => setShowToast(false)} 
        message={toastMessage} 
        type={toastType}
      />
      <PrintStyles />
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{isEditMode ? 'Edit Report for' : 'Create Report for'} {patient.name}</h2>
        <div>
          <Link to={isEditMode && patient?.id ? `/patients/${patient.id}` : `/patients/${patientId}`}>
            <Button variant="outline-secondary">
              <FaTimes className="me-2" /> Cancel
            </Button>
          </Link>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Row>
        <Col md={8}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Report Details</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Doctor *</Form.Label>
                      <Form.Select
                        name="doctor"
                        value={formData.doctor}
                        onChange={handleInputChange}
                        required
                      >
                        {doctors.map(doctor => (
                          <option key={doctor.id} value={doctor.id}>
                            {doctor.name} - {doctor.specialization}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Follow Up Date</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <FaCalendarAlt />
                        </InputGroup.Text>
                        <Form.Control
                          type="date"
                          name="followUpDate"
                          value={formData.followUpDate}
                          onChange={handleInputChange}
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Blood Pressure</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          placeholder="Systolic"
                          value={formData.bloodPressure.systolic}
                          onChange={(e) => setFormData({
                            ...formData,
                            bloodPressure: {
                              ...formData.bloodPressure,
                              systolic: e.target.value ? Number(e.target.value) : ''
                            }
                          })}
                        />
                        <InputGroup.Text>/</InputGroup.Text>
                        <Form.Control
                          type="number"
                          placeholder="Diastolic"
                          value={formData.bloodPressure.diastolic}
                          onChange={(e) => setFormData({
                            ...formData,
                            bloodPressure: {
                              ...formData.bloodPressure,
                              diastolic: e.target.value ? Number(e.target.value) : ''
                            }
                          })}
                        />
                        <InputGroup.Text>mmHg</InputGroup.Text>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Diagnosis *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="diagnosis"
                    value={formData.diagnosis}
                    onChange={handleInputChange}
                    placeholder="Enter diagnosis"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Doctor's Advice</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="doctorAdvice"
                    value={formData.doctorAdvice}
                    onChange={handleInputChange}
                    placeholder="Enter doctor's advice for patient"
                  />
                </Form.Group>

                <hr />

                <h5 className="mb-3">Medicines</h5>

                <Row className="mb-3">
                  <Col md={6}>
                    <InputGroup>
                      <Form.Control
                        placeholder="Search medicine..."
                        value={medicineSearch}
                        onChange={e => setMedicineSearch(e.target.value)}
                      />
                      <Button variant="outline-secondary" disabled>
                        <FaSearch />
                      </Button>
                    </InputGroup>

                    {searchResults.length > 0 && (
                      <ListGroup className="mt-2 search-results">
                        {searchResults.map(medicine => (
                          <ListGroup.Item
                            key={medicine._id || medicine.id}
                            action
                            onClick={() => {
                              console.log("Selected medicine from search:", medicine);
                              // Get the ID of the medicine, handling both _id and id fields
                              const medicineId = medicine._id || medicine.id;
                              
                              // Create a complete new medication object with all details
                              const newMedicationWithDetails = {
                                ...newMedication,
                                medicine: medicineId,
                                medicineName: medicine.name,
                                medicineDetails: {
                                  ...medicine,
                                  // Ensure required fields exist even if API doesn't provide them
                                  stock: medicine.stock || 0,
                                  unit: medicine.unit || 'units',
                                  name: medicine.name
                                },
                                dosage: {
                                  morning: false,
                                  afternoon: false,
                                  evening: false,
                                  night: false,
                                  morningBeforeFood: false,
                                  morningAfterFood: false,
                                  afternoonBeforeFood: false,
                                  afternoonAfterFood: false,
                                  eveningBeforeFood: false,
                                  eveningAfterFood: false,
                                  nightBeforeFood: false,
                                  nightAfterFood: false
                                }
                              };
                              
                              // Set the complete object
                              setNewMedication(newMedicationWithDetails);
                              
                              // Update the search field to show the selected medicine name
                              setMedicineSearch(medicine.name);
                              
                              // Clear search results once a selection is made
                              setSearchResults([]);
                            }}
                          >
                            {medicine.name} - {medicine.type || medicine.category || ''}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                  </Col>
                  <Col md={6}>
                    <Form.Select
                      value={newMedication.medicine}
                      onChange={e => handleNewMedicationChange('medicine', e.target.value)}
                    >
                      <option value="">Select Medicine</option>
                      {console.log("Rendering medicines dropdown with data:", medicines)}
                      {Array.isArray(medicines) && medicines.length > 0 ? (
                        medicines.map(medicine => (
                          <option key={medicine._id || medicine.id} value={medicine._id || medicine.id}>
                            {medicine.name} - {medicine.type || medicine.category || ''}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>No medicines available</option>
                      )}
                    </Form.Select>
                  </Col>
                </Row>

                {newMedication.medicine && (
                  <Card className="mb-3 bg-light">
                    <Card.Body>
                      {error && error.includes('Not enough stock') && (
                        <Alert variant="danger" className="mt-1 mb-3">
                          <FaTimes className="me-2" />
                          {error}
                        </Alert>
                      )}
                      
                      <h6>Dosage</h6>
                      <Row className="mb-2">
                        <Col xs={12} sm={6} md={3} className="mb-3">
                          <Form.Check
                            type="checkbox"
                            id="morning"
                            label="Morning"
                            checked={newMedication.dosage.morning}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setNewMedication(prev => ({
                                ...prev,
                                dosage: {
                                  ...prev.dosage,
                                  morning: checked,
                                  morningBeforeFood: checked ? prev.dosage.morningBeforeFood : false,
                                  morningAfterFood: checked ? prev.dosage.morningAfterFood : false
                                }
                              }));
                            }}
                          />
                          {newMedication.dosage.morning && (
                            <div className="ms-4 mt-2">
                              <Form.Check
                                type="checkbox"
                                id="morning-before"
                                label="Before food"
                                checked={newMedication.dosage.morningBeforeFood}
                                onChange={(e) => {
                                  setNewMedication(prev => ({
                                    ...prev,
                                    dosage: {
                                      ...prev.dosage,
                                      morningBeforeFood: e.target.checked
                                    }
                                  }));
                                }}
                              />
                              <Form.Check
                                type="checkbox"
                                id="morning-after"
                                label="After food"
                                checked={newMedication.dosage.morningAfterFood}
                                onChange={(e) => {
                                  setNewMedication(prev => ({
                                    ...prev,
                                    dosage: {
                                      ...prev.dosage,
                                      morningAfterFood: e.target.checked
                                    }
                                  }));
                                }}
                              />
                            </div>
                          )}
                        </Col>
                        <Col xs={12} sm={6} md={3} className="mb-3">
                          <Form.Check
                            type="checkbox"
                            id="afternoon"
                            label="Afternoon"
                            checked={newMedication.dosage.afternoon}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setNewMedication(prev => ({
                                ...prev,
                                dosage: {
                                  ...prev.dosage,
                                  afternoon: checked,
                                  afternoonBeforeFood: checked ? prev.dosage.afternoonBeforeFood : false,
                                  afternoonAfterFood: checked ? prev.dosage.afternoonAfterFood : false
                                }
                              }));
                            }}
                          />
                          {newMedication.dosage.afternoon && (
                            <div className="ms-4 mt-2">
                              <Form.Check
                                type="checkbox"
                                id="afternoon-before"
                                label="Before food"
                                checked={newMedication.dosage.afternoonBeforeFood}
                                onChange={(e) => {
                                  setNewMedication(prev => ({
                                    ...prev,
                                    dosage: {
                                      ...prev.dosage,
                                      afternoonBeforeFood: e.target.checked
                                    }
                                  }));
                                }}
                              />
                              <Form.Check
                                type="checkbox"
                                id="afternoon-after"
                                label="After food"
                                checked={newMedication.dosage.afternoonAfterFood}
                                onChange={(e) => {
                                  setNewMedication(prev => ({
                                    ...prev,
                                    dosage: {
                                      ...prev.dosage,
                                      afternoonAfterFood: e.target.checked
                                    }
                                  }));
                                }}
                              />
                            </div>
                          )}
                        </Col>
                        <Col xs={12} sm={6} md={3} className="mb-3">
                          <Form.Check
                            type="checkbox"
                            id="evening"
                            label="Evening"
                            checked={newMedication.dosage.evening}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setNewMedication(prev => ({
                                ...prev,
                                dosage: {
                                  ...prev.dosage,
                                  evening: checked,
                                  eveningBeforeFood: checked ? prev.dosage.eveningBeforeFood : false,
                                  eveningAfterFood: checked ? prev.dosage.eveningAfterFood : false
                                }
                              }));
                            }}
                          />
                          {newMedication.dosage.evening && (
                            <div className="ms-4 mt-2">
                              <Form.Check
                                type="checkbox"
                                id="evening-before"
                                label="Before food"
                                checked={newMedication.dosage.eveningBeforeFood}
                                onChange={(e) => {
                                  setNewMedication(prev => ({
                                    ...prev,
                                    dosage: {
                                      ...prev.dosage,
                                      eveningBeforeFood: e.target.checked
                                    }
                                  }));
                                }}
                              />
                              <Form.Check
                                type="checkbox"
                                id="evening-after"
                                label="After food"
                                checked={newMedication.dosage.eveningAfterFood}
                                onChange={(e) => {
                                  setNewMedication(prev => ({
                                    ...prev,
                                    dosage: {
                                      ...prev.dosage,
                                      eveningAfterFood: e.target.checked
                                    }
                                  }));
                                }}
                              />
                            </div>
                          )}
                        </Col>
                        <Col xs={12} sm={6} md={3} className="mb-3">
                          <Form.Check
                            type="checkbox"
                            id="night"
                            label="Night"
                            checked={newMedication.dosage.night}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setNewMedication(prev => ({
                                ...prev,
                                dosage: {
                                  ...prev.dosage,
                                  night: checked,
                                  nightBeforeFood: checked ? prev.dosage.nightBeforeFood : false,
                                  nightAfterFood: checked ? prev.dosage.nightAfterFood : false
                                }
                              }));
                            }}
                          />
                          {newMedication.dosage.night && (
                            <div className="ms-4 mt-2">
                              <Form.Check
                                type="checkbox"
                                id="night-before"
                                label="Before food"
                                checked={newMedication.dosage.nightBeforeFood}
                                onChange={(e) => {
                                  setNewMedication(prev => ({
                                    ...prev,
                                    dosage: {
                                      ...prev.dosage,
                                      nightBeforeFood: e.target.checked
                                    }
                                  }));
                                }}
                              />
                              <Form.Check
                                type="checkbox"
                                id="night-after"
                                label="After food"
                                checked={newMedication.dosage.nightAfterFood}
                                onChange={(e) => {
                                  setNewMedication(prev => ({
                                    ...prev,
                                    dosage: {
                                      ...prev.dosage,
                                      nightAfterFood: e.target.checked
                                    }
                                  }));
                                }}
                              />
                            </div>
                          )}
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Duration</Form.Label>
                            <Form.Control
                              type="text"
                              value={newMedication.duration}
                              onChange={e => handleNewMedicationChange('duration', e.target.value)}
                              placeholder="e.g., 5 days, 1 week"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Additional Instructions</Form.Label>
                            <Form.Control
                              type="text"
                              value={newMedication.instructions}
                              onChange={e =>
                                handleNewMedicationChange('instructions', e.target.value)
                              }
                              placeholder="Special instructions"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      


                      <div className="d-grid">
                        <Button variant="primary" onClick={addMedication}>
                          <FaPlus className="me-2" /> Add Medicine
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {formData.medications.length > 0 && (
                  <Table responsive bordered className="mt-3">
                    <thead>
                      <tr>
                        <th>Medicine</th>
                        <th>Dosage</th>
                        <th>Duration</th>
                        <th>Instructions</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.medications.map((med, index) => {
                        // Get proper medicine details using the same logic as above
                        let medicineName = med.medicineName;
                        let medicineDetails = med.medicineDetails;
                        
                        // Convert to string for comparison
                        const medicineId = med.medicine;
                        
                        // Try to find medicine in the medicines array if name not available
                        if (!medicineName) {
                          const foundMedicine = Array.isArray(medicines) ? medicines.find(m => 
                            (m._id && String(m._id) === String(medicineId)) || 
                            (m.id && String(m.id) === String(medicineId))
                          ) : null;
                          
                          if (foundMedicine) {
                            medicineName = foundMedicine.name;
                            medicineDetails = foundMedicine;
                          } else if (typeof med.medicine === 'object' && med.medicine.name) {
                            medicineName = med.medicine.name;
                          } else {
                            medicineName = 'Unknown';
                            console.error("Print view: Medicine not found for ID:", medicineId);
                          }
                        }
                        
                        return (
                          <tr key={index}>
                            <td style={{ border: '1px solid #000', padding: '4px' }}>
                              {medicineName}
                            </td>
                            <td style={{ border: '1px solid #000', padding: '4px' }}>
                              {formatDosage(med.dosage)}
                            </td>
                            <td style={{ border: '1px solid #000', padding: '4px' }}>{med.duration}</td>
                            <td style={{ border: '1px solid #000', padding: '4px' }}>{med.instructions}</td>
                            <td>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => removeMedication(index)}
                              >
                                <FaTimes />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                )}

                <hr />
                
                <h5 className="mb-3">Doctor Signatures</h5>
                <p className="text-muted mb-3">Select up to 3 doctors who need to sign this report</p>
                
                <Row className="mb-4">
                  {doctors.map(doc => (
                    <Col md={4} key={doc.id}>
                      <Form.Check 
                        type="checkbox"
                        id={`doctor-${doc.id}`}
                        label={`Dr. ${doc.name}`}
                        checked={formData.signedByDoctors.includes(doc.id)}
                        disabled={!formData.signedByDoctors.includes(doc.id) && formData.signedByDoctors.length >= 3}
                        onChange={e => {
                          if (e.target.checked) {
                            if (formData.signedByDoctors.length < 3) {
                              setFormData({
                                ...formData,
                                signedByDoctors: [...formData.signedByDoctors, doc.id]
                              });
                            }
                          } else {
                            setFormData({
                              ...formData,
                              signedByDoctors: formData.signedByDoctors.filter(id => id !== doc.id)
                            });
                          }
                        }}
                      />
                    </Col>
                  ))}
                </Row>

                <Card className="mb-4">
                  <Card.Header className="bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0">Ultrasound Report</h5>
                      <Form.Check
                        type="switch"
                        id="ultrasound-toggle"
                        checked={formData.ultrasoundReport.hasUltrasound}
                        onChange={toggleUltrasoundReport}
                        label="Include Ultrasound Report"
                      />
                    </div>
                  </Card.Header>
                  {formData.ultrasoundReport.hasUltrasound && (
                    <Card.Body>
                      <Form.Group className="mb-3">
                        <Form.Label>Ultrasound Type</Form.Label>
                        <Form.Select
                          value={formData.ultrasoundReport.type}
                          onChange={(e) => handleUltrasoundChange('type', e.target.value)}
                        >
                          <option value="Abdominal">Abdominal</option>
                          <option value="Pelvic">Pelvic</option>
                          <option value="Obstetric">Obstetric</option>
                          <option value="Transvaginal">Transvaginal</option>
                          <option value="Renal">Renal</option>
                          <option value="Thyroid">Thyroid</option>
                          <option value="Breast">Breast</option>
                          <option value="Scrotal">Scrotal</option>
                          <option value="Prostate">Prostate</option>
                          <option value="Carotid">Carotid</option>
                          <option value="Vascular">Vascular</option>
                          <option value="Musculoskeletal">Musculoskeletal</option>
                          <option value="Cardiac">Cardiac</option>
                          <option value="Other">Other</option>
                        </Form.Select>
                      </Form.Group>
                      
                      {renderUltrasoundForm()}
                    </Card.Body>
                  )}
                </Card>

                <div className="d-flex justify-content-between mt-4">
                  <Button variant="success" type="submit" className="w-100" disabled={saving}>
                    {saving ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" /> {isEditMode ? 'Update' : 'Save'} & Print Report
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Patient Information</h5>
            </Card.Header>
            <Card.Body>
              <p>
                <strong>Patient ID:</strong> <span className="patient-id">{patient.patientId}</span>
              </p>
              <p>
                <strong>Name:</strong> {patient.name}
              </p>
              <p>
                <strong>Age:</strong> {patient.age} | <strong>Gender:</strong> {patient.gender}
              </p>
              {patient.bloodGroup && (
                <p>
                  <strong>Blood Group:</strong> {patient.bloodGroup}
                </p>
              )}
              <p>
                <strong>Contact:</strong> {patient.contactNumber}
              </p>
              {patient.address && (
                <p>
                  <strong>Address:</strong> {patient.address}
                </p>
              )}
              {patient.history && (
                <>
                  <strong>Medical History:</strong>
                  <p className="text-muted">{patient.history}</p>
                </>
              )}
            </Card.Body>
          </Card>

          {/* Hidden report preview for printing */}
          <div style={{ display: 'none' }}>
            <div ref={reportRef} className="print-view" style={{ padding: '15px', maxWidth: '100%', fontSize: '11px' }}>
              
              {/* PAGE 1: Medical Report */}
              <div className="first-page-content" style={{ pageBreakAfter: 'always' }}>
                <div className="clinic-header mb-3" style={{ display: 'flex', alignItems: 'center', borderBottom: '2px solid #333', paddingBottom: '10px' }}>
                {/* Clinic logo */}
                <div style={{ width: '80px', marginRight: '15px' }}>
                  <img 
                    src={clinic?.logo ? `${window.location.origin}${clinic.logo}` : "/logo.png"} 
                    alt={clinic?.name || "Clinic Logo"} 
                    style={{ maxWidth: '100%', height: 'auto' }} 
                  />
                </div>
                {/* Clinic name with stylish font */}
                <div>
                  <h2 style={{ 
                    margin: '0', 
                    fontFamily: 'Georgia, serif', 
                    color: '#1a5276', 
                    fontWeight: 'bold',
                    letterSpacing: '1px'
                  }}>
                    {clinic?.name || 'Your Clinic Name'}
                  </h2>
                  <p style={{ margin: '2px 0 0 0', fontSize: '12px' }}>{clinic?.address || 'Address'} | {clinic?.contactNumber || 'Contact Information'}</p>
                </div>
              </div>

              <div className="report-content">
                <div className="report-header" style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h2 style={{ margin: '0', fontSize: '16px' }}>Medical Report</h2>
                      <div className="patient-info" style={{ marginTop: '5px' }}>
                        <p style={{ margin: '2px 0' }}><strong>Patient Name:</strong> {patient?.name}</p>
                        <p style={{ margin: '2px 0' }}><strong>Age:</strong> {patient?.age} | <strong>Gender:</strong> {patient?.gender}</p>
                        {patient?.bloodGroup && (
                          <p style={{ margin: '2px 0' }}><strong>Blood Group:</strong> {patient?.bloodGroup}</p>
                        )}
                        <p style={{ margin: '2px 0' }}><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
                    </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ margin: '2px 0', fontSize: '11px' }}><strong>Doctor:</strong> {formData.doctor && doctors.find(d => d.id === formData.doctor)?.name}</p>
                      {formData.followUpDate && (
                        <p style={{ margin: '2px 0', fontSize: '11px' }}><strong>Follow-up:</strong> {new Date(formData.followUpDate).toLocaleDateString()}</p>
                      )}
                      {formData.billNumber && (
                        <p style={{ margin: '2px 0', fontSize: '11px' }}><strong>Bill #:</strong> {formData.billNumber}</p>
                      )}
                      {formData.bloodPressure && formData.bloodPressure.systolic && formData.bloodPressure.diastolic && (
                        <p style={{ margin: '2px 0', fontSize: '11px' }}><strong>Blood Pressure:</strong> {formData.bloodPressure.systolic}/{formData.bloodPressure.diastolic} mmHg</p>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  {/* Left Column - Medicines and Injections */}
                  <div style={{ flex: '1.2' }}>
                    {/* Medicines Section */}
                    {formData.medications.length > 0 && (
                      <div className="medicines-section" style={{ marginBottom: '10px' }}>
                        <h3 style={{ fontSize: '12px', marginBottom: '5px' }}>Medications</h3>
                        <table className="medication-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left' }}>Medicine</th>
                              <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left' }}>Dosage</th>
                              <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left' }}>Duration</th>
                              <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left' }}>Instructions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {formData.medications.map((med, index) => {
                              const medicineDetails = Array.isArray(medicines) ? medicines.find(m => m._id === med.medicine) : null;
                              return (
                                <tr key={index}>
                                  <td style={{ border: '1px solid #000', padding: '4px' }}>
                                    {medicineDetails ? medicineDetails.name : 'Unknown'}
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '4px' }}>
                                    {formatDosage(med.dosage)}
                                  </td>
                                  <td style={{ border: '1px solid #000', padding: '4px' }}>{med.duration}</td>
                                  <td style={{ border: '1px solid #000', padding: '4px' }}>{med.instructions}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Diagnosis and Advice */}
                  <div style={{ flex: '0.8' }}>
                    <div className="diagnosis-section" style={{ marginBottom: '10px' }}>
                      <h3 style={{ fontSize: '12px', marginBottom: '5px' }}>Diagnosis</h3>
                      <p style={{ margin: '0', fontSize: '11px' }}>{formData.diagnosis}</p>
                    </div>

                    <div className="advice-section">
                      <h3 style={{ fontSize: '12px', marginBottom: '5px' }}>Doctor's Advice</h3>
                      <p style={{ margin: '0', fontSize: '11px' }}>{formData.doctorAdvice}</p>
                    </div>
                  </div>
                </div>

                {/* Doctor Signatures */}
                <div className="doctor-signatures">
                  <div className="d-flex justify-content-around">
                    {formData.signedByDoctors.map((doctorId, index) => {
                      const doctor = doctors.find(d => d.id === doctorId);
                      return doctor ? (
                        <div key={index} className="doctor-signature">
                          <div style={{ borderTop: '1px solid #000', width: '100px' }}></div>
                          <p className="mb-0 mt-1" style={{ fontSize: '10px' }}>Dr. {doctor.name}</p>
                          <p className="small" style={{ fontSize: '9px' }}>{doctor.qualification || ''}</p>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              </div>
              
              {/* END OF PAGE 1 */}
              </div>
              
              {/* PAGE 2: Ultrasound Report */}
              {formData.ultrasoundReport && formData.ultrasoundReport.hasUltrasound && (
                <div className="ultrasound-report-section" style={{ pageBreakBefore: 'always' }}>
                  <div className="clinic-header mb-3" style={{ display: 'flex', alignItems: 'center', borderBottom: '2px solid #333', paddingBottom: '10px' }}>
                    <div style={{ width: '80px', marginRight: '15px' }}>
                      <img 
                        src={clinic?.logo ? `${window.location.origin}${clinic.logo}` : "/logo.png"} 
                        alt={clinic?.name || "Clinic Logo"} 
                        style={{ maxWidth: '100%', height: 'auto' }} 
                      />
                    </div>
                    <div>
                      <h2 style={{ 
                        margin: '0', 
                        fontFamily: 'Georgia, serif', 
                        color: '#1a5276', 
                        fontWeight: 'bold',
                        letterSpacing: '1px'
                      }}>
                        {clinic?.name || 'Your Clinic Name'}
                      </h2>
                      <p style={{ margin: '2px 0 0 0', fontSize: '12px' }}>{clinic?.address || 'Address'} | {clinic?.contactNumber || 'Contact Information'}</p>
                    </div>
                  </div>
                  
                  <h3 style={{ textAlign: 'center', fontSize: '14px', marginBottom: '10px' }}>
                    {formData.ultrasoundReport.type} Ultrasound Report
                  </h3>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                    <div>
                      <p style={{ margin: '2px 0', fontSize: '11px' }}><strong>Patient:</strong> {patient?.name}</p>
                      <p style={{ margin: '2px 0', fontSize: '11px' }}><strong>Age/Gender:</strong> {patient?.age}/{patient?.gender}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ margin: '2px 0', fontSize: '11px' }}><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
                      <p style={{ margin: '2px 0', fontSize: '11px' }}><strong>Doctor:</strong> {formData.doctor && doctors.find(d => d.id === formData.doctor)?.name}</p>
                      {formData.billNumber && (
                        <p style={{ margin: '2px 0', fontSize: '11px' }}><strong>Bill #:</strong> {formData.billNumber}</p>
                      )}
                      {formData.bloodPressure && formData.bloodPressure.systolic && formData.bloodPressure.diastolic && (
                        <p style={{ margin: '2px 0', fontSize: '11px' }}><strong>Blood Pressure:</strong> {formData.bloodPressure.systolic}/{formData.bloodPressure.diastolic} mmHg</p>
                      )}
                    </div>
                  </div>
                  
                  {formData.ultrasoundReport.type === 'Obstetric' && (
                    <div style={{ marginBottom: '15px' }}>
                      <h4 style={{ fontSize: '12px', marginBottom: '5px' }}>Fetal Biometry</h4>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                        <thead>
                          <tr>
                            <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left', backgroundColor: '#f4f4f4' }}>Parameter</th>
                            <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left', backgroundColor: '#f4f4f4' }}>Measurement</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.ultrasoundReport.gestationalAge && (
                            <tr>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>Gestational Age</td>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>{formData.ultrasoundReport.gestationalAge}</td>
                            </tr>
                          )}
                          {formData.ultrasoundReport.biparietalDiameter && (
                            <tr>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>Biparietal Diameter (BPD)</td>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>{formData.ultrasoundReport.biparietalDiameter} mm</td>
                            </tr>
                          )}
                          {formData.ultrasoundReport.headCircumference && (
                            <tr>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>Head Circumference (HC)</td>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>{formData.ultrasoundReport.headCircumference} mm</td>
                            </tr>
                          )}
                          {formData.ultrasoundReport.abdominalCircumference && (
                            <tr>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>Abdominal Circumference (AC)</td>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>{formData.ultrasoundReport.abdominalCircumference} mm</td>
                            </tr>
                          )}
                          {formData.ultrasoundReport.femurLength && (
                            <tr>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>Femur Length (FL)</td>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>{formData.ultrasoundReport.femurLength} mm</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      
                      <h4 style={{ fontSize: '12px', marginBottom: '5px', marginTop: '15px' }}>Obstetric Parameters</h4>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                        <thead>
                          <tr>
                            <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left', backgroundColor: '#f4f4f4' }}>Parameter</th>
                            <th style={{ border: '1px solid #000', padding: '4px', textAlign: 'left', backgroundColor: '#f4f4f4' }}>Finding</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.ultrasoundReport.fetalHeartRate && (
                            <tr>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>Fetal Heart Rate</td>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>{formData.ultrasoundReport.fetalHeartRate} bpm</td>
                            </tr>
                          )}
                          {formData.ultrasoundReport.gestationalSac && (
                            <tr>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>Gestational Sac (GS)</td>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>{formData.ultrasoundReport.gestationalSac} mm</td>
                            </tr>
                          )}
                          {formData.ultrasoundReport.crownRumpLength && (
                            <tr>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>Crown-Rump Length (CRL)</td>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>{formData.ultrasoundReport.crownRumpLength} mm</td>
                            </tr>
                          )}
                          {formData.ultrasoundReport.cardiacFlicker && (
                            <tr>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>Cardiac Flicker</td>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>{formData.ultrasoundReport.cardiacFlicker}</td>
                            </tr>
                          )}
                          {formData.ultrasoundReport.fetalPresentation && (
                            <tr>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>Fetal Presentation</td>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>{formData.ultrasoundReport.fetalPresentation}</td>
                            </tr>
                          )}
                          {formData.ultrasoundReport.fetalLie && (
                            <tr>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>Fetal Lie</td>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>{formData.ultrasoundReport.fetalLie}</td>
                            </tr>
                          )}
                          {formData.ultrasoundReport.fetalMovement && (
                            <tr>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>Fetal Movement</td>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>{formData.ultrasoundReport.fetalMovement}</td>
                            </tr>
                          )}
                          {formData.ultrasoundReport.cardiacActivity && (
                            <tr>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>Cardiac Activity</td>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>{formData.ultrasoundReport.cardiacActivity}</td>
                            </tr>
                          )}
                          {formData.ultrasoundReport.placentaPosition && (
                            <tr>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>Placenta Position</td>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>{formData.ultrasoundReport.placentaPosition}</td>
                            </tr>
                          )}
                          {formData.ultrasoundReport.amniotic && (
                            <tr>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>Amniotic Fluid</td>
                              <td style={{ border: '1px solid #000', padding: '4px' }}>{formData.ultrasoundReport.amniotic}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {formData.ultrasoundReport.type === 'Abdominal' && (
                    <div style={{ marginBottom: '15px' }}>
                      <h4 style={{ fontSize: '12px', marginBottom: '5px' }}>Organ-Specific Findings</h4>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                        <tbody>
                          {formData.ultrasoundReport.liver && (
                            <tr>
                              <th style={{ border: '1px solid #ddd', padding: '3px', width: '30%', textAlign: 'left' }}>Liver</th>
                              <td style={{ border: '1px solid #ddd', padding: '3px' }}>{formData.ultrasoundReport.liver}</td>
                            </tr>
                          )}
                          {formData.ultrasoundReport.gallbladder && (
                            <tr>
                              <th style={{ border: '1px solid #ddd', padding: '3px', textAlign: 'left' }}>Gallbladder</th>
                              <td style={{ border: '1px solid #ddd', padding: '3px' }}>{formData.ultrasoundReport.gallbladder}</td>
                            </tr>
                          )}
                          {formData.ultrasoundReport.pancreas && (
                            <tr>
                              <th style={{ border: '1px solid #ddd', padding: '3px', textAlign: 'left' }}>Pancreas</th>
                              <td style={{ border: '1px solid #ddd', padding: '3px' }}>{formData.ultrasoundReport.pancreas}</td>
                            </tr>
                          )}
                          {formData.ultrasoundReport.spleen && (
                            <tr>
                              <th style={{ border: '1px solid #ddd', padding: '3px', textAlign: 'left' }}>Spleen</th>
                              <td style={{ border: '1px solid #ddd', padding: '3px' }}>{formData.ultrasoundReport.spleen}</td>
                            </tr>
                          )}
                          {formData.ultrasoundReport.kidneys && (
                            <tr>
                              <th style={{ border: '1px solid #ddd', padding: '3px', textAlign: 'left' }}>Kidneys</th>
                              <td style={{ border: '1px solid #ddd', padding: '3px' }}>{formData.ultrasoundReport.kidneys}</td>
                            </tr>
                          )}
                          {formData.ultrasoundReport.otherStructures && (
                            <tr>
                              <th style={{ border: '1px solid #ddd', padding: '3px', textAlign: 'left' }}>Other Structures</th>
                              <td style={{ border: '1px solid #ddd', padding: '3px' }}>{formData.ultrasoundReport.otherStructures}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {formData.ultrasoundReport.findings && (
                    <div style={{ marginBottom: '15px' }}>
                      <h4 style={{ fontSize: '12px', marginBottom: '5px' }}>Findings</h4>
                      <p style={{ margin: '0', fontSize: '11px', border: '1px solid #ddd', padding: '5px' }}>
                        {formData.ultrasoundReport.findings}
                      </p>
                    </div>
                  )}
                  
                  {/* Display ultrasound images */}
                  {formData.ultrasoundReport.images && formData.ultrasoundReport.images.length > 0 && (
                    <div className="ultrasound-images" style={{ width: '100%' }}>
                      <h4 style={{ fontSize: '12px', marginBottom: '10px' }}>Ultrasound Images</h4>
                      
                      <table style={{ width: '100%', border: 'none', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        <tbody style={{ display: 'block', width: '100%' }}>
                          {/* Process images in pairs */}
                          {Array.from({ length: Math.ceil(formData.ultrasoundReport.images.length / 2) }).map((_, rowIndex) => (
                            <tr key={rowIndex} style={{ display: 'flex', width: '100%', flexDirection: 'row' }}>
                              {/* First image in pair */}
                              {formData.ultrasoundReport.images[rowIndex * 2] && (
                                <td style={{ width: '50%', display: 'inline-block', padding: '5px', textAlign: 'center', verticalAlign: 'top', float: 'left' }}>
                                  <img
                                    src={window.location.origin + formData.ultrasoundReport.images[rowIndex * 2].path}
                                    alt={`Ultrasound image ${rowIndex * 2 + 1}`}
                                    style={{ 
                                      width: '100%',
                                      maxWidth: '100%',
                                      height: 'auto',
                                      objectFit: 'contain',
                                      border: '1px solid #ddd',
                                      padding: '4px',
                                      backgroundColor: '#f8f9fa',
                                      display: 'block',
                                      margin: '0 auto'
                                    }}
                                  />
                                  {formData.ultrasoundReport.images[rowIndex * 2].caption && (
                                    <p style={{ 
                                      margin: '5px 0 0 0',
                                      fontSize: '10px',
                                      fontStyle: 'italic',
                                      textAlign: 'center'
                                    }}>
                                      {formData.ultrasoundReport.images[rowIndex * 2].caption}
                                    </p>
                                  )}
                                </td>
                              )}
                              
                              {/* Second image in pair */}
                              {formData.ultrasoundReport.images[rowIndex * 2 + 1] && (
                                <td style={{ width: '50%', display: 'inline-block', padding: '5px', textAlign: 'center', verticalAlign: 'top', float: 'left' }}>
                                  <img
                                    src={window.location.origin + formData.ultrasoundReport.images[rowIndex * 2 + 1].path}
                                    alt={`Ultrasound image ${rowIndex * 2 + 2}`}
                                    style={{ 
                                      width: '100%',
                                      maxWidth: '100%',
                                      height: 'auto',
                                      objectFit: 'contain',
                                      border: '1px solid #ddd',
                                      padding: '4px',
                                      backgroundColor: '#f8f9fa',
                                      display: 'block',
                                      margin: '0 auto'
                                    }}
                                  />
                                  {formData.ultrasoundReport.images[rowIndex * 2 + 1].caption && (
                                    <p style={{ 
                                      margin: '5px 0 0 0',
                                      fontSize: '10px',
                                      fontStyle: 'italic',
                                      textAlign: 'center'
                                    }}>
                                      {formData.ultrasoundReport.images[rowIndex * 2 + 1].caption}
                                    </p>
                                  )}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {formData.ultrasoundReport.impression && (
                    <div style={{ marginBottom: '15px' }}>
                      <h4 style={{ fontSize: '12px', marginBottom: '5px' }}>Impression</h4>
                      <p style={{ margin: '0', fontSize: '11px', border: '1px solid #ddd', padding: '5px' }}>
                        {formData.ultrasoundReport.impression}
                      </p>
                    </div>
                  )}
                  
                  {formData.ultrasoundReport.recommendations && (
                    <div style={{ marginBottom: '15px' }}>
                      <h4 style={{ fontSize: '12px', marginBottom: '5px' }}>Recommendations</h4>
                      <p style={{ margin: '0', fontSize: '11px', border: '1px solid #ddd', padding: '5px' }}>
                        {formData.ultrasoundReport.recommendations}
                      </p>
                    </div>
                  )}
                  
                  {/* Doctor Signatures */}
                  <div className="doctor-signatures">
                    <div className="d-flex justify-content-around">
                      {formData.signedByDoctors.map((doctorId, index) => {
                        const doctor = doctors.find(d => d.id === doctorId);
                        return doctor ? (
                          <div key={index} className="doctor-signature">
                            <div style={{ borderTop: '1px solid #000', width: '100px' }}></div>
                            <p className="mb-0 mt-1" style={{ fontSize: '10px' }}>Dr. {doctor.name}</p>
                            <p className="small" style={{ fontSize: '9px' }}>{doctor.qualification || ''}</p>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="report-footer">
                <div className="footer-credit">
                  This software is Developed by Abdullah Arif | Contact: +92-3137323866 | Email: abdullaharif7323@gmail.com
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default CreateReport; 
